<template>
  <LoadSpinner v-if="loading" />
  <div
    v-else
    class="listing-card-grid"
  >
    <ul v-if="hasPagination" class="campaign-items-list__pagination">
      <li class="campaign-items-list__prev">
        <button v-if="canPrevPage()" @click="goToPreviousPage()"><</button>
        <span v-else><</span>
      </li>
      <li v-for="pageNumber in totalPages" :key="pageNumber">
        <button
          v-if="!isCurrentPage(pageNumber)"
          @click="goToPage(pageNumber)"
        >
          {{ pageNumber }}
        </button>
        <span v-else> {{ pageNumber }}</span>
      </li>
      <li class="campaign-items-list__next">
        <button v-if="canNextPage()" @click="goToNextPage()">></button>
        <span v-else>></span>
      </li>
    </ul>
    <div v-if="hasItems" class="listing-card-grid__grid .campaign-items-list__grid">
      <div
        v-for="apartment in currentPageData"
        class="listing-card-grid__column campaign-items-list__column"
        :key="apartment.id"
      >
        <ApartmentCard
          :apartment="apartment"
          :get-params="{back: true}"
          class="listing-card-grid__item"
        />
      </div>
    </div>
    <div v-else>
      Ei löydy tuloksia "{{ code }}" kampanjakoodilla.
    </div>
    <ul v-if="hasPagination" class="campaign-items-list__pagination">
      <li>
        <button v-if="canPrevPage()" @click="goToPreviousPage()"><</button>
        <span v-else><</span>
      </li>
      <li v-for="pageNumber in totalPages" :key="pageNumber">
        <button
          v-if="!isCurrentPage(pageNumber)"
          @click="goToPage(pageNumber)"
        >
          {{ pageNumber }}
        </button>
        <span v-else> {{ pageNumber }}</span>
      </li>
      <li>
        <button v-if="canNextPage()" @click="goToNextPage()">></button>
        <span v-else>></span>
      </li>
    </ul>
  </div>
</template>

<script>
  import axios from 'axios';

  import globals from '../lib/globals';
  import LoadSpinner from './components/LoadSpinner';
  import HousingLocalityCard from './components/HousingLocalityCard';
  import ApartmentCard from './components/ApartmentCard';

  export default {
    components: {HousingLocalityCard, LoadSpinner, ApartmentCard},
    props: {
      group: {
        type: String,
        default: 'old',
      },
      code: {
        type: String,
        default: '',
      },
      itemsPerPage: {
        type: Number,
        default: 20,
      },
      currentPage: {
        type: Number,
        default: 1,
      },
      totalItems: {
        type: Number,
        default: 0,
      }
    },
    /**
     * Retrieves the initial data for the component.
     *
     * @return {object} The initial data object
     */
    data() {
      return {
        loading: false,
        apartments: [],
      };
    },
    computed: {
      /**
       * Calculate the total number of pages based on the length of apartments and itemsPerPage.
       *
       * @return {number} The total number of pages.
       */
      totalPages() {
        if(!Array.isArray(this.apartments) || this.apartments.length === 0) {
          return 0;
        }
        return Math.ceil(this.apartments.length / this.itemsPerPage);
      },
      /**
       * Retrieves the data for the current page based on the current page number and items per page.
       *
       * @return {Array} The data for the current page
       */
      currentPageData() {
        const startIndex = (this.currentPage - 1) * this.itemsPerPage;
        const endIndex = startIndex + Number(this.itemsPerPage);

        if(!Array.isArray(this.apartments) || this.apartments.length === 0) {
          return [];
        }

        console.log(startIndex, endIndex);
        return this.apartments.slice(startIndex, endIndex);
      },
      /**
       * Check if there are items in the apartments array.
       *
       * @return {boolean} true if there are items, false otherwise
       */
       hasItems() {
        return this.apartments.length > 0;
      },
      /**
       * Check if pagination is needed based on total items and items per page.
       *
       * @return {Boolean} true if pagination is needed, false otherwise
       */
      hasPagination() {
        return this.apartments.length > 0 && this.totalItems > this.itemsPerPage;
      }
    },
    methods: {
      /**
       * Sets the current page to the specified page number.
       *
       * @param {number} pageNumber - The page number to navigate to
       * @return {void}
       */
      goToPage(pageNumber) {
        this.currentPage = pageNumber;
      },
      /**
       * Check if the given page number is the current page.
       *
       * @param {number} pageNumber - The page number to check
       * @return {boolean} Returns true if the given page number is the current page, otherwise false
       */
      isCurrentPage(pageNumber) {
        return this.currentPage === pageNumber;
      },
      /**
       * A function to check if it is possible to go to the previous page.
       *
       * @return {boolean} true if it is possible to go to the previous page, false otherwise
       */
      canPrevPage() {
        return this.currentPage > 1;
      },
      /**
       * Checks if the current page is less than the total pages.
       *
       * @return {boolean} true if the current page is less than the total pages, false otherwise
       */
      canNextPage() {
        return this.currentPage < this.totalPages;
      },
      /**
       * Function to go to the previous page.
       *
       */
      goToPreviousPage() {
        this.goToPage(this.currentPage - 1);
      },
      /**
       * goToNextPage - A function to go to the next page.
       *
       */
      goToNextPage() {
        this.goToPage(this.currentPage + 1);
      }
    },

    /**
     * Asynchronous function to perform tasks before mounting the component.
     *
     * @return {void} No return value
     */
    async beforeMount() {
      this.loading = true;
      const url = `${globals.urlIntegrationApi}/public/available-apartments-by-campaign-code/${this.code}`;
      const response = await axios.get(url);
      this.apartments = response?.data;
      this.totalItems = this.apartments.length;
      this.loading = false;
    },
  };
</script>

<style lang="scss">
  @import "~styles/definitions";

  .campaign-items-list {
    &__pagination {
      display: flex;
      justify-content: center;
      align-items: center;
      list-style: none;
      margin: 0;
      padding: 0;
      margin-block: 1rem;

      & > li {
        margin-inline: 0.25rem;

        & > button {
          background: transparent;
          border: 0;
          padding: 0;
          cursor: pointer;
          color: $color-green-2025;
          font-size: 1rem;
        }
        & > span {
          font-size: 1.1rem;
        }
      }
    }
  }
</style>
